import _get from 'lodash/get';
import { helpingMethod } from '../../../utils/HelpingMethod';
import { MetaOption } from '../../../types';
interface RobItem {
  id: string | number;
  srno: number;
  vessel: string;
  manager: string;
  port: string;
  date: string;
  type: string;
  remarks: string;
  vesselId: number;
}

const robsApiParser: any = {
  RobsListParser: (res: any) => {
    if (!res) return { data: [], pagination: { totalElements: 0 } };
    // Parsing data
    const data: RobItem[] = res?.data?.content?.map((itm: any) => {
      const date = _get(itm, 'eventDate', '');
      const formattedDate = date ? helpingMethod.formatDate(date) : '';

      // Map backend type codes to user-friendly display names
      const rawType = _get(itm, 'type', '');
      let displayType = rawType;
      if (rawType === 'STD') displayType = 'STANDARD';
      if (rawType === 'ACQ') displayType = 'ACQUISITION';
      if (rawType === 'NEW') displayType = 'NEW BUILDING';

      return {
        id: _get(itm, 'id', 0),
        vessel: _get(itm, 'vesselName', ''),
        vesselId: _get(itm, 'vesselId', 0),
        manager: _get(itm, 'managerName', ''),
        port: _get(itm, 'port', ''),
        date: formattedDate,
        type: helpingMethod.formatChipLabel(displayType),
        remarks: _get(itm, 'comments', ''),
      };
    });

    const paginationData = res?.data?.pageable;
    return { data, pagination: paginationData };
  },

  ProductsByVesselParser: (res: any): RobProduct[] => {
    if (!res) return [];

    // Parsing data
    const data: RobProduct[] = res?.data?.content?.map((itm: any) => ({
      id: _get(itm, 'productId', 0),
      name: _get(itm, 'productName', '-'),
      category: _get(itm, 'categoryName', '-'),
      type: _get(itm, 'typeName', '-') === '' ? '-' : _get(itm, 'typeName', '-'),
      maker: _get(itm, 'makerName', '-'),
      isTypeIn: _get(itm, 'typeIn', false),
      quantity: _get(itm, 'quantity', ''),
      quantityUnit: _get(itm, 'unitSymbol', ''),
      netValue: _get(itm, 'netValue', ''),
      netValueUnit: _get(itm, 'unitSymbol', ''),
      packagingCost: _get(itm, 'packagingCost', ''),
      packagingCostUnit: _get(itm, 'packagingCostUnit', ''),
      updatedBy: _get(itm, 'updatedBy', 0),
      isSumpTankMandatory: _get(itm, 'sumpTank', false),
    }));

    return data;
  },

  managersParser: (res: any): any[] => {
    if (!res) return [];

    // Parsing data
    const data: any[] = res?.data?.content?.map((itm: any) => ({
      value: _get(itm, 'mangerId', 0),
      label: _get(itm, 'shortName', ''),
    }));

    return data;
  },

  vesselsParser: (res: any): any[] => {
    if (!res) return [];

    // Parsing data
    const data: any[] = res?.data?.content?.map((itm: any) => ({
      value: _get(itm, 'vesselId', 0),
      label: _get(itm, 'vesselName', ''),
    }));

    return data;
  },

  UpdatedProductsByVesselParser: (res: any) => {
    if (!res) return [];

    const data: RobProduct[] = res?.data?.map((itm: any) => ({
      id: _get(itm, 'productId', 0),
      name: _get(itm, 'productName', '-'),
      category: _get(itm, 'categoryName', '-'),
      type: _get(itm, 'typeName', '-') === null ? '-' : _get(itm, 'typeName', '-'),
      maker: _get(itm, 'makerName', '-'),
      isTypeIn: _get(itm, 'typeIn', false),
      quantity: _get(itm, 'storageQuantity', ''),
      netValue: _get(itm, 'sumpTankQuantity', ''),
      valueOfStorageQuantity: _get(itm, 'valueOfStorageQuantity', ''),
      quantityUnit: _get(itm, 'symbol', ''),
      netValueUnit: _get(itm, 'symbol', ''),
      isSumpTankMandatory: _get(itm, 'sumpTank', false),
    }));

    return data;
  },

  vesselsByManagerParser: (res: any) => {
    if (!res) return [];

    const data = res?.data?.map((itm: any) => ({
      value: _get(itm, 'vesselId', 0),
      label: _get(itm, 'vesselName', ''),
    }));

    return data;
  },

  availableDatesParser: (res: any): { startDate: string; endDate: string } => {
    if (!res) return { startDate: '', endDate: '' };
    const data = res?.map((itm: any) => {
      return {
        startDate: helpingMethod.formatDate(_get(itm, 'dateFrom', '')),
        endDate: helpingMethod.formatDate(_get(itm, 'dateTo', '')),
        id: _get(itm, 'id', null),
      };
    });

    return data?.[0] || {};
  },

  vesselsFilterOptionsParser: (res: any): MetaOption[] => {
    if (!res) return [];

    // Parsing data
    const data: MetaOption[] = res?.data?.map((itm: any) => ({
      label: _get(itm, 'name', ''),
      value: _get(itm, 'id', 0),
    }));

    return data;
  },

  managersFilterOptionsParser: (res: any): MetaOption[] => {
    if (!res) return [];

    // Parsing data
    const data: MetaOption[] = res?.data?.map((itm: any) => ({
      label: _get(itm, 'shortName', ''),
      value: _get(itm, 'id', 0),
    }));

    return data;
  },

  typesFilterOptionsParser: (res: any): string[] => {
    if (!res) return [];

    // Parsing data - map backend codes to display names
    const data = res?.data?.map((itm: any) => {
      if (itm === 'STD') {
        return 'Standard';
      }
      if (itm === 'ACQ') {
        return 'Acquisition';
      }
      if (itm === 'NEW') {
        return 'New building';
      }
      if (itm === 'EOM') {
        return 'Eom';
      }
      return itm;
    });

    return data;
  },
};

export default robsApiParser;
export interface RobProduct {
  id: number;
  name: string;
  category: string;
  type: string;
  maker: string;
  isTypeIn: boolean;
  quantity: string;
  quantityUnit: string;
  netValue: string;
  netValueUnit: string;
  packagingCost: string;
  packagingCostUnit: string;
  updatedBy: number;
  storageQuantity: string;
  sumpTankQuantity: string;
  valueOfStorageQuantity: string;
}

export interface Rob {
  id: string;
  type: string;
  vessel: string;
  supplier: string;
  port: string;
  date: string | null;
  poNumber: string;
  invoiceNumber: string;
  salesTax: string;
  localTax: string;
  status: string;
  products: RobProduct[];
  createdAt: string;
  updatedAt?: string;
}
